import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { getProductFromGeneralKey } from '@/utils/productGetServerSideProps';
import { getProductDetail } from '@/service/ProductCommon/getProductDetail';
import { useGlobal } from '@/utils/useGlobal';

function processDeep(input: any, callback: (key: string, value: any) => void) {
    for (const [key, value] of Object.entries(input)) {
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (typeof parsed === 'object' && parsed !== null) {
                    processDeep(parsed, callback);
                    continue;
                }
            } catch {
                // 不是 JSON 字符串，直接回调
            }
            callback(key, value);
        } else if (typeof value === 'object' && value !== null) {
            processDeep(value, callback);
        } else {
            callback(key, value);
        }
    }
}

const NULL_SYMBOL = '-';

export const useSpec = (GeneralKey?: string[], keys?: string[]) => {
    const { gloproductlist } = useGlobal();
    const { locale = 'us' } = useRouter();

    const getData = async (generalKey: string) => {
        return await new Promise(async (resolve, reject) => {
            const product = getProductFromGeneralKey({ gloproductlist, generalKey });
            const res = await getProductDetail(product?.url, locale);
            if (res?.code === '0') {
                resolve({ productCatagory: res?.data?.productCatagoryResponseList, productDetail: res?.data?.spuResponse });
            } else {
                reject(res?.message);
            }
        });
    };

    const [spaces, setSpaces] = useState<any>([]);
    useEffect(() => {
        const promises: any[] = [];
        GeneralKey?.map((generalKey: string) => promises.push(getData(generalKey)));
        Promise.all(promises).then((res: any[]) => {
            const curSpaces: { title: string; desc: string }[][] = [];
            res.forEach((i, idx) => {
                if (i.productDetail?.skuList && i.productDetail.skuList.length > 0) {
                    const specParamList = i?.productCatagory?.[0]?.specGroupList?.flatMap((j) => j?.specParamList);
                    i.productDetail?.skuList?.forEach?.((j, k) => {
                        if (k === 0) {
                            const spaceItem: { title: string; desc: string; generalKey: string }[] = [];
                            const ownSpec = JSON.parse(j.ownSpec);
                            processDeep(ownSpec, (key, value) => {
                                const specParam = specParamList.find((k) => k.specParamId === key);
                                let specV = value;
                                if (specParam?.fieldType === 'boolean') {
                                    specV = !value ? NULL_SYMBOL : ['false'].includes(value) ? NULL_SYMBOL : '✓';
                                }
                                if (!Number.isNaN(Number(value))) {
                                    specV = value + (specParam?.unit || '');
                                }
                                if (keys) {
                                    if (keys.includes(specParam.generalKey)) {
                                        spaceItem.push({ title: specV, desc: specParam.name, generalKey: specParam.generalKey });
                                    } else {
                                        // 被筛掉的数据
                                        // console.log(specV, specParam.generalKey, specParam.name);
                                    }
                                } else {
                                    spaceItem.push({ title: specV, desc: specParam.name, generalKey: specParam.generalKey });
                                }
                            });
                            curSpaces.push(spaceItem);
                        }
                    });
                } else {
                    // 没有请求到数据放回数组
                    curSpaces.push([]);
                }
            });

            // 构建全量 generalKey 集合
            const allDescsSet = new Set<string>();
            curSpaces.forEach((spaceItem) => {
                spaceItem.forEach(({ generalKey }) => {
                    allDescsSet.add(generalKey);
                });
            });
            // 根据 keys 顺序排序 desc，其余补充在后面并按字母排序
            const orderedKeys = keys || [];
            const sortedGeneralKeys = [
                ...orderedKeys.filter((k) => allDescsSet.has(k)),
                ...Array.from(allDescsSet)
                    .filter((k) => !orderedKeys.includes(k))
                    .sort(),
            ];
            // 对齐所有 spaceItem 的规格顺序
            const normalizedSpaces = curSpaces.map((spaceItem) => {
                const descMap = new Map(spaceItem.map(({ generalKey, title, desc }) => [generalKey, { title, desc }]));
                return sortedGeneralKeys.map((key) => {
                    const item = descMap.get(key);
                    return {
                        desc: item?.desc || key,
                        title: item?.title ?? '–',
                    };
                });
            });
            setSpaces(normalizedSpaces);
        });
    }, [GeneralKey]);

    return spaces;
};
